(function () {
  var CONFIG = {
    smoothing: 0.24,
    maxDevicePixelRatio: 2,
    mobileBreakpoint: 734,
    snapThreshold: 0.985
  };

  var sections = [];
  var raf = null;
  var resizeTimeout = null;
  var lastScrollY = window.pageYOffset || document.documentElement.scrollTop || 0;
  var isAutoScrolling = false;
  var lastWindowWidth = window.innerWidth;

  function clamp(value, min, max) {
    return Math.min(Math.max(value, min), max);
  }

  function easeOutCubic(value) {
    return 1 - Math.pow(1 - value, 3);
  }

  function isMobileViewport() {
    return window.innerWidth <= CONFIG.mobileBreakpoint;
  }

  function getScrollY() {
    return window.pageYOffset || document.documentElement.scrollTop || 0;
  }

  function getViewportHeight() {
    return window.innerHeight || document.documentElement.clientHeight;
  }

  function getStableViewportHeight(state) {
    var viewportHeight = getViewportHeight();

    if (!isMobileViewport()) {
      state.lockedMobileViewportHeight = null;
      state.lockedMobileViewportWidth = null;
      return viewportHeight;
    }

    if (!state.lockedMobileViewportHeight) {
      state.lockedMobileViewportHeight = viewportHeight;
      state.lockedMobileViewportWidth = window.innerWidth;
    }

    return state.lockedMobileViewportHeight;
  }

  function shouldRefreshMobileViewportLock(state) {
    if (!isMobileViewport()) return false;
    if (!state.lockedMobileViewportWidth) return true;

    return Math.abs(window.innerWidth - state.lockedMobileViewportWidth) > 40;
  }

  function getReleasePercentage(state) {
    return clamp(parseFloat(state.section.dataset.fixedReleasePercentage) || 0, 0, 90);
  }

  function getFixedReleaseFactor(state) {
    return clamp(1 - getReleasePercentage(state) / 100, 0.1, 1);
  }

  function updateSectionHeight(state, forceMobileRefresh) {
    if (forceMobileRefresh || shouldRefreshMobileViewportLock(state)) {
      state.lockedMobileViewportHeight = null;
      state.lockedMobileViewportWidth = null;
    }

    var viewportHeight = getStableViewportHeight(state);
    var desktopScroll = parseFloat(state.section.dataset.scrollDesktop) || 175;
    var mobileScroll = parseFloat(state.section.dataset.scrollMobile) || 100;
    var scrollValue = isMobileViewport() ? mobileScroll : desktopScroll;
    var releaseFactor = getFixedReleaseFactor(state);
    var baseScrollHeight = viewportHeight * (scrollValue / 100);
    var fixedScrollHeight = baseScrollHeight * releaseFactor;
    var totalHeight = viewportHeight + fixedScrollHeight;

    state.viewportHeight = viewportHeight;
    state.scrollHeight = fixedScrollHeight;
    state.baseScrollHeight = baseScrollHeight;

    state.section.style.setProperty(
      '--section-animated-video-viewport-height',
      viewportHeight.toFixed(2) + 'px'
    );

    state.section.style.setProperty(
      '--section-animated-video-total-height',
      totalHeight.toFixed(2) + 'px'
    );
  }

  function splitUrl(url) {
    var hashSplit = url.split('#');
    var hash = hashSplit[1] ? '#' + hashSplit.slice(1).join('#') : '';
    var withoutHash = hashSplit[0];

    var querySplit = withoutHash.split('?');
    var query = querySplit[1] ? '?' + querySplit.slice(1).join('?') : '';
    var path = querySplit[0];

    return {
      path: path,
      query: query,
      hash: hash
    };
  }

  function buildSheetUrl(firstSheetUrl, sheetNumber) {
    var parts = splitUrl(firstSheetUrl);

    var path = parts.path.replace(/(\d+)(\.[a-zA-Z0-9]+)$/, function (match, number, extension) {
      return String(sheetNumber).padStart(number.length, '0') + extension;
    });

    return path + parts.query + parts.hash;
  }

  function getProgress(section, state) {
    var rect = section.getBoundingClientRect();
    var computedStyle = window.getComputedStyle(section);
    var viewportHeight = parseFloat(
      computedStyle.getPropertyValue('--section-animated-video-viewport-height')
    );

    if (!viewportHeight) {
      viewportHeight = getViewportHeight();
    }

    var releasePercentage = getReleasePercentage(state);
    var releaseProgress = releasePercentage / 100;
    var fixedProgress = 1 - releaseProgress;

    var sectionScrollY = -rect.top;
    var fixedScrollDistance = Math.max(rect.height - viewportHeight, 1);
    var releasedScrollDistance = Math.max(viewportHeight, 1);

    if (releasePercentage <= 0) {
      return clamp(sectionScrollY / fixedScrollDistance, 0, 1);
    }

    if (sectionScrollY <= fixedScrollDistance) {
      return clamp((sectionScrollY / fixedScrollDistance) * fixedProgress, 0, fixedProgress);
    }

    var releasedProgress = (sectionScrollY - fixedScrollDistance) / releasedScrollDistance;

    return clamp(fixedProgress + releasedProgress * releaseProgress, 0, 1);
  }

  function isSectionNearViewport(section) {
    var rect = section.getBoundingClientRect();
    var viewportHeight = getViewportHeight();

    return rect.bottom > -viewportHeight * 0.25 && rect.top < viewportHeight * 1.25;
  }

  function getElementTop(element) {
    return Math.ceil(element.getBoundingClientRect().top + getScrollY()) + 4;
  }

  function getNextSectionTarget(section) {
    var shopifyWrapper = section.closest('.shopify-section');
    var currentElement = shopifyWrapper || section;
    var nextElement = currentElement.nextElementSibling;

    while (nextElement) {
      if (nextElement.offsetParent !== null && nextElement.offsetHeight > 0) {
        return nextElement;
      }

      nextElement = nextElement.nextElementSibling;
    }

    return null;
  }

  function isScrollToNextEnabled(state) {
    if (isMobileViewport()) {
      return state.scrollToNextMobile;
    }

    return state.scrollToNextDesktop;
  }

  function scrollToNextSection(state) {
    if (isAutoScrolling) return;
    if (state.hasSnappedToNext) return;
    if (!isScrollToNextEnabled(state)) return;

    var nextSection = getNextSectionTarget(state.section);

    if (!nextSection) return;

    state.hasSnappedToNext = true;
    isAutoScrolling = true;

    window.scrollTo({
      top: getElementTop(nextSection),
      behavior: 'auto'
    });

    window.setTimeout(function () {
      isAutoScrolling = false;
      lastScrollY = getScrollY();
    }, 300);
  }

  function getVariantSettings(state) {
    var useMobile = isMobileViewport() && state.spriteSheetMobileUrl;

    if (useMobile) {
      return {
        key: 'mobile',
        firstSheetUrl: state.spriteSheetMobileUrl,
        sheetCount: state.sheetCountMobile,
        columns: state.columnsMobile,
        rows: state.rowsMobile
      };
    }

    return {
      key: 'desktop',
      firstSheetUrl: state.spriteSheetDesktopUrl,
      sheetCount: state.sheetCountDesktop,
      columns: state.columnsDesktop,
      rows: state.rowsDesktop
    };
  }

  function applyVariantSettings(state) {
    var settings = getVariantSettings(state);

    var changed =
      state.activeVariantKey !== settings.key ||
      state.firstSheetUrl !== settings.firstSheetUrl ||
      state.sheetCount !== settings.sheetCount ||
      state.columns !== settings.columns ||
      state.rows !== settings.rows;

    state.activeVariantKey = settings.key;
    state.firstSheetUrl = settings.firstSheetUrl;
    state.sheetCount = settings.sheetCount;
    state.columns = settings.columns;
    state.rows = settings.rows;

    if (changed) {
      state.loadedBaseUrl = '';
      state.sheets = [];
      state.ready = false;
      state.drawnFrame = -1;
      state.section.classList.remove('is-ready');
    }

    return changed;
  }

  function resizeCanvas(state) {
    var rect = state.canvas.getBoundingClientRect();

    if (!rect.width || !rect.height) return;

    var ratio = Math.min(window.devicePixelRatio || 1, CONFIG.maxDevicePixelRatio);
    var width = Math.round(rect.width * ratio);
    var height = Math.round(rect.height * ratio);

    if (state.canvas.width !== width || state.canvas.height !== height) {
      state.canvas.width = width;
      state.canvas.height = height;
      state.drawnFrame = -1;
      drawCurrentFrame(state);
    }
  }

  function drawCover(ctx, image, canvas, sx, sy, sw, sh) {
    var canvasWidth = canvas.width;
    var canvasHeight = canvas.height;

    if (!canvasWidth || !canvasHeight || !sw || !sh) return;

    var sourceRatio = sw / sh;
    var canvasRatio = canvasWidth / canvasHeight;

    var cropX = sx;
    var cropY = sy;
    var cropW = sw;
    var cropH = sh;

    if (sourceRatio > canvasRatio) {
      cropW = cropH * canvasRatio;
      cropX = sx + (sw - cropW) / 2;
    } else {
      cropH = cropW / canvasRatio;
      cropY = sy + (sh - cropH) / 2;
    }

    ctx.clearRect(0, 0, canvasWidth, canvasHeight);
    ctx.imageSmoothingEnabled = true;

    if ('imageSmoothingQuality' in ctx) {
      ctx.imageSmoothingQuality = 'high';
    }

    ctx.drawImage(
      image,
      cropX,
      cropY,
      cropW,
      cropH,
      0,
      0,
      canvasWidth,
      canvasHeight
    );
  }

  function getLoadedSheet(state, sheetIndex) {
    var sheet = state.sheets[sheetIndex];

    if (sheet && sheet.complete && sheet.naturalWidth) {
      return sheet;
    }

    return null;
  }

  function drawFrame(state, frameIndex) {
    frameIndex = clamp(frameIndex, 0, state.totalFrames - 1);

    if (state.drawnFrame === frameIndex) return;

    var framesPerSheet = state.columns * state.rows;
    var sheetIndex = Math.floor(frameIndex / framesPerSheet);
    var frameInSheet = frameIndex % framesPerSheet;
    var sheet = getLoadedSheet(state, sheetIndex);

    if (!sheet) return;

    var column = frameInSheet % state.columns;
    var row = Math.floor(frameInSheet / state.columns);

    var frameWidth = sheet.naturalWidth / state.columns;
    var frameHeight = sheet.naturalHeight / state.rows;

    var sx = column * frameWidth;
    var sy = row * frameHeight;

    state.drawnFrame = frameIndex;

    drawCover(
      state.ctx,
      sheet,
      state.canvas,
      sx,
      sy,
      frameWidth,
      frameHeight
    );
  }

  function drawCurrentFrame(state) {
    drawFrame(state, Math.round(state.currentFrame));
  }

  function updateTextBlocks(state) {
    var currentProgress = state.currentFrame / (state.totalFrames - 1);

    state.textBlocks.forEach(function (textBlock) {
      var startProgress = textBlock.startProgress / 100;
      var endProgress = textBlock.endProgress / 100;

      if (endProgress <= startProgress) {
        endProgress = startProgress + 0.01;
      }

      var range = endProgress - startProgress;
      var fadeDistance = Math.min(0.08, range / 2);

      var fadeInEnd = startProgress + fadeDistance;
      var fadeOutStart = endProgress - fadeDistance;

      var opacity = 0;
      var translateY = 36;

      if (currentProgress >= startProgress && currentProgress <= endProgress) {
        if (currentProgress <= fadeInEnd) {
          var fadeInProgress = clamp(
            (currentProgress - startProgress) / fadeDistance,
            0,
            1
          );

          opacity = easeOutCubic(fadeInProgress);
          translateY = (1 - opacity) * 36;
        } else if (currentProgress >= fadeOutStart) {
          var fadeOutProgress = clamp(
            (endProgress - currentProgress) / fadeDistance,
            0,
            1
          );

          opacity = easeOutCubic(fadeOutProgress);
          translateY = (1 - opacity) * -36;
        } else {
          opacity = 1;
          translateY = 0;
        }
      }

      textBlock.element.style.setProperty('--animated-video-text-opacity', opacity.toFixed(4));
      textBlock.element.style.setProperty('--animated-video-text-y', translateY.toFixed(2) + 'px');
    });
  }

  function updateScrollIndicator(state, progress) {
    if (!state.scrollIndicator) return;

    if (!isMobileViewport()) {
      state.section.classList.add('has-started-scrolling');
      return;
    }

    if (progress > 0.002) {
      state.section.classList.add('has-started-scrolling');
    } else {
      state.section.classList.remove('has-started-scrolling');
    }
  }

  function updateState(state, immediate) {
    var progress = getProgress(state.section, state);
    var targetFrame = progress * (state.totalFrames - 1);
    var difference = targetFrame - state.currentFrame;

    updateScrollIndicator(state, progress);

    if (progress < 0.75) {
      state.hasSnappedToNext = false;
    }

    if (immediate || Math.abs(difference) < 0.01) {
      state.currentFrame = targetFrame;
      drawCurrentFrame(state);
      updateTextBlocks(state);
      return false;
    }

    state.currentFrame += difference * CONFIG.smoothing;

    drawCurrentFrame(state);
    updateTextBlocks(state);

    return Math.abs(difference) > 0.01;
  }

  function render() {
    raf = null;

    var keepRendering = false;

    sections.forEach(function (state) {
      if (!state.ready) return;

      if (updateState(state, false)) {
        keepRendering = true;
      }
    });

    if (keepRendering) {
      requestRender();
    }
  }

  function requestRender() {
    if (raf) return;

    raf = window.requestAnimationFrame(render);
  }

  function loadSheets(state) {
    applyVariantSettings(state);

    if (!state.firstSheetUrl || state.loadedBaseUrl === state.firstSheetUrl) return;

    state.loadedBaseUrl = state.firstSheetUrl;
    state.sheets = [];
    state.ready = false;
    state.drawnFrame = -1;
    state.section.classList.remove('is-ready');

    for (var index = 0; index < state.sheetCount; index++) {
      (function (sheetIndex) {
        var image = new Image();

        image.decoding = 'async';

        if ('fetchPriority' in image) {
          image.fetchPriority = sheetIndex === 0 ? 'high' : 'low';
        }

        image.onload = function () {
          if (sheetIndex === 0) {
            state.ready = true;
            state.section.classList.add('is-ready');

            if (isSectionNearViewport(state.section)) {
              resizeCanvas(state);
              updateState(state, true);
            }
          }

          requestRender();
        };

        image.src = buildSheetUrl(state.firstSheetUrl, sheetIndex + 1);

        state.sheets[sheetIndex] = image;
      })(index);
    }
  }

  function getTextBlocks(section) {
    return Array.prototype.slice
      .call(section.querySelectorAll('[data-animated-video-text]'))
      .map(function (element) {
        return {
          element: element,
          startProgress: parseInt(element.dataset.startProgress, 10) || 0,
          endProgress: parseInt(element.dataset.endProgress, 10) || 100
        };
      });
  }

  function initSection(section) {
    if (section.dataset.sectionAnimatedVideoInitialized === 'true') return;

    var canvas = section.querySelector('.section-animated-video__canvas');
    var spriteSheetDesktopUrl = section.dataset.spriteSheetDesktop || section.dataset.spriteSheet;
    var spriteSheetMobileUrl = section.dataset.spriteSheetMobile || '';

    if (!canvas || !spriteSheetDesktopUrl) return;

    var sheetCountDesktop = parseInt(section.dataset.sheetCount, 10) || 20;
    var columnsDesktop = parseInt(section.dataset.sheetColumns, 10) || 2;
    var rowsDesktop = parseInt(section.dataset.sheetRows, 10) || 3;

    var sheetCountMobile = parseInt(section.dataset.sheetCountMobile, 10) || 14;
    var columnsMobile = parseInt(section.dataset.sheetColumnsMobile, 10) || 3;
    var rowsMobile = parseInt(section.dataset.sheetRowsMobile, 10) || 3;

    var totalFrames = parseInt(section.dataset.totalFrames, 10) || 120;

    section.dataset.sectionAnimatedVideoInitialized = 'true';

    var state = {
      section: section,
      canvas: canvas,
      ctx: canvas.getContext('2d'),

      spriteSheetDesktopUrl: spriteSheetDesktopUrl,
      spriteSheetMobileUrl: spriteSheetMobileUrl,

      firstSheetUrl: '',
      loadedBaseUrl: '',
      activeVariantKey: '',

      sheets: [],
      textBlocks: getTextBlocks(section),
      scrollIndicator: section.querySelector('[data-animated-video-scroll-indicator]'),

      currentFrame: 0,
      drawnFrame: -1,
      ready: false,

      sheetCountDesktop: sheetCountDesktop,
      columnsDesktop: columnsDesktop,
      rowsDesktop: rowsDesktop,

      sheetCountMobile: sheetCountMobile,
      columnsMobile: columnsMobile,
      rowsMobile: rowsMobile,

      sheetCount: sheetCountDesktop,
      columns: columnsDesktop,
      rows: rowsDesktop,
      totalFrames: totalFrames,

      scrollToNextDesktop: section.dataset.scrollToNextDesktop === 'true',
      scrollToNextMobile: section.dataset.scrollToNextMobile === 'true',
      hasSnappedToNext: false,

      lockedMobileViewportHeight: null,
      lockedMobileViewportWidth: null,
      viewportHeight: 0,
      scrollHeight: 0,
      baseScrollHeight: 0
    };

    sections.push(state);

    applyVariantSettings(state);
    updateSectionHeight(state, true);
    resizeCanvas(state);
    loadSheets(state);
  }

  function init() {
    document
      .querySelectorAll('[data-section-animated-video]')
      .forEach(initSection);

    requestRender();
  }

  function handleResize() {
    window.clearTimeout(resizeTimeout);

    resizeTimeout = window.setTimeout(function () {
      if (isAutoScrolling) return;

      var currentWindowWidth = window.innerWidth;
      var widthChanged = Math.abs(currentWindowWidth - lastWindowWidth) > 40;

      sections.forEach(function (state) {
        var changedVariant = applyVariantSettings(state);

        if (changedVariant) {
          loadSheets(state);
        }

        updateSectionHeight(state, widthChanged);
        resizeCanvas(state);
        updateState(state, true);
      });

      lastWindowWidth = currentWindowWidth;

      requestRender();
    }, 80);
  }

  function handleScroll() {
    var currentScrollY = getScrollY();
    var isScrollingDown = currentScrollY > lastScrollY;

    sections.forEach(function (state) {
      if (!state.ready) return;

      var progress = getProgress(state.section, state);

      updateState(state, false);

      if (
        isScrollingDown &&
        progress >= CONFIG.snapThreshold &&
        !isAutoScrolling
      ) {
        scrollToNextSection(state);
      }
    });

    lastScrollY = currentScrollY;

    requestRender();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  window.addEventListener('scroll', handleScroll, { passive: true });
  window.addEventListener('resize', handleResize);

  document.addEventListener('shopify:section:load', function () {
    init();
    handleResize();
  });
})();